import React, { useCallback } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { useSynapse } from 'app/lib/synaptik';
import { styled, Text, Box, Flex } from '@goodrx/matisse';
import * as Analytics from 'app/lib/analytics';
import { PageHeading } from 'app/components/PageHeading';
import DocumentTitle from 'app/components/core/document-title';
import { AuthLayout } from 'app/components/auth/AuthLayout/AuthLayout';
import { Divider } from 'app/components/core/Divider';
import { CdnImage } from 'app/components/CdnImage';
import { CtaContainer } from 'app/components/auth/SignUp/CtaContainer/CtaContainer';
import { SignInCta } from 'app/components/auth/SignUp/SignInCta';
import { HeadingWrapper } from 'app/components/auth/SignUp/HeadingWrapper';
import { SignUpForm, HandleSignUpProps } from '../SignUpForm';

const StyledDivider = styled(Divider)`
  margin: 24px 0;
`;

interface SignUpPageProps extends RouteComponentProps {
  title?: string;
  subtitle?: string;
  icon?: string;
  signUp?: (profile: { email: string; password: string; consentedToHma: boolean }) => void;
}

export const SignUpPage: React.FC<SignUpPageProps> = ({
  location,
  signUp: passedSignUp,
  icon,
  title = 'Sign Up',
  subtitle = 'Start your online medical visit by creating an account.',
}) => {
  const [signUp, signingUp] = useSynapse(({ user }) => [passedSignUp || user.signUp, user.state.signingUp]);

  const handleSignUp = useCallback(
    async ({ values, isValid }: HandleSignUpProps) => {
      const { email, password, consentedToHma } = values;

      if (!isValid || !email || !password) return;

      try {
        await signUp({ email, password, consentedToHma: !!consentedToHma });

        if (consentedToHma) {
          Analytics.onHmaConsent({ source: 'Sign Up' });
        }
        Analytics.trackEvent('Registration CTA Clicked', { buttonName: 'Create Account', page: 'Email Password' });
      } catch (error) {}
    },
    [signUp]
  );

  const isDrugSelectionFlow = title !== 'Sign Up';

  return (
    <>
      <CtaContainer>
        <SignInCta small />
      </CtaContainer>
      <AuthLayout>
        <DocumentTitle titleKey="SignUp" />

        {isDrugSelectionFlow ? (
          <Flex>
            <Box color="greyDarkest" mb={0}>
              <Text as="h1" m={0} fontSize={22}>
                {title}
              </Text>

              {subtitle && (
                <Text as="p" color="greyDarkest" mt="8px" mb={0} fontWeight={500}>
                  {subtitle}
                </Text>
              )}
            </Box>
            {icon && (
              <Box ml={3}>
                <CdnImage src={icon} alt="sign-up-illustration" />
              </Box>
            )}
          </Flex>
        ) : (
          <HeadingWrapper>
            <CdnImage src="/images/illustrations/sign-up.svg" alt="sign-up-illustration" />
            <PageHeading title={title} subtitle={subtitle} color="greyDarkest" mb={0} />
          </HeadingWrapper>
        )}

        <StyledDivider />
        {isDrugSelectionFlow && (
          <Text as="p" mb={3} fontSize={16} fontWeight={800}>
            Create an account to start your visit:
          </Text>
        )}
        <SignUpForm onSubmit={handleSignUp} isLoading={signingUp} initialEmail={location.state?.email} />
      </AuthLayout>
    </>
  );
};

export const SignUpPageWithRouter = withRouter(SignUpPage);
